import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  EVAvatar,
  EVBox,
  EVTypography,
  EVKeyboardArrowDown,
  EVBadge,
} from '@eagleview/ev-comp-library';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import UserMenu from './components/UserMenu.component';
import UserProfileErrorAvatar from './UserProfileErrorAvatar.component';
import useStyles from './UserProfileStyle';
import BadgeContent from './components/BadgeContent.component';

/**
 * Component that shows a monogram and the name of the user
 */
const UserProfile = (props) => {
  const {
    monogramVariant, userDetails, withUserMenu, setViewProfile, initiatorDetails, isImpersonated, t, userProfileError, isGuestUser,
  } = props;
  const classes = useStyles();
  const { firstName, lastName } = userDetails;
  const { initiatorFirstName = '', initiatorLastName = '' } = initiatorDetails || {};
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const initials = (firstName ? firstName.charAt(0).toUpperCase() : '') + (lastName ? lastName.charAt(0).toUpperCase() : '');
  const avatarRef = React.useRef();

  /**
   * Opens the context menu
   */
  const handleClick = () => {
    if (userProfileError) return; // Don't open menu if there's an error
    setAnchorEl(avatarRef.current);
    setOpen(true);
  };

  /**
   * Closes the context menu
   */
  const handleMenuClickAway = () => {
    setOpen(false);
  };

  // If there's a user profile error, show a dummy non-clickable avatar
  if (userProfileError) {
    return <UserProfileErrorAvatar monogramVariant={monogramVariant} />;
  }

  // Guest user (isResourceShared && !isEvUser): first letter of email/username in avatar, email below (from user profile v2), menu with only Logout.
  if (isGuestUser && withUserMenu) {
    const rawEmail = userDetails.email || userDetails.userName || '';
    const guestInitial = rawEmail.charAt(0).toUpperCase() || '--';
    const displayEmail = rawEmail ? rawEmail.charAt(0).toUpperCase() + rawEmail.slice(1).toLowerCase() : '--';
    return (
      <div className={classes.container}>
        <EVBox className={classes.box}>
          <EVAvatar
            id="userAvatar"
            ref={avatarRef}
            className={clsx(
              { [classes.avatarMicro]: monogramVariant === 'micro' },
              { [classes.avatarSmall]: monogramVariant === 'small' },
              { [classes.avatarMedium]: monogramVariant === 'medium' },
              { [classes.avatarLarge]: monogramVariant === 'large' },
            )}
            onClick={handleClick}
          >
            {guestInitial}
          </EVAvatar>
          { monogramVariant === 'medium' && (
            <EVKeyboardArrowDown id="keyboardArrowDown" className={classes.keyboardArrowDown} onClick={handleClick} />
          )}
        </EVBox>
        { monogramVariant === 'medium' && (
          <EVTypography id="fullName" className={classes.guestName}>
            {displayEmail}
          </EVTypography>
        )}
        <UserMenu
          setViewProfile={setViewProfile}
          handleMenuClickAway={handleMenuClickAway}
          anchorEl={anchorEl}
          open={open}
          monogramVariant={monogramVariant}
          userDetails={userDetails}
          isImpersonated={false}
          isSignOutDisabled={false}
          isGuestUser
        />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <EVBox className={classes.box}>
        <EVBadge
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          badgeContent={isImpersonated && (monogramVariant === 'medium' || monogramVariant === 'small') ? <BadgeContent /> : null}
        >
          <EVAvatar
            id="userAvatar"
            ref={avatarRef}
            className={clsx(
              { [classes.avatarMicro]: monogramVariant === 'micro' },
              { [classes.avatarSmall]: monogramVariant === 'small' },
              { [classes.avatarMedium]: monogramVariant === 'medium' },
              { [classes.avatarLarge]: monogramVariant === 'large' },
            )}
            onClick={handleClick}
          >
            {initials}
          </EVAvatar>
        </EVBadge>
        { monogramVariant === 'medium' && (
          <EVKeyboardArrowDown id="keyboardArrowDown" className={classes.keyboardArrowDown} onClick={handleClick} />
        )}
      </EVBox>
      { monogramVariant === 'medium' && (
      <div className={classes.nameContainer}>
        <EVTypography id="fullName" className={classes.name}>
          {isImpersonated ? (
            <>
              <span id="impersonated-username" className={classes.boldText}>
                {`${initiatorFirstName} ${initiatorLastName} `}
              </span>
              <span className={classes.signIntext}>
                {t('SignedInAsText')}
              </span>
              <br />
              {`${firstName} ${lastName}`}
            </>
          ) : (
            `${firstName} ${lastName}`
          )}
        </EVTypography>
      </div>
      )}
      { withUserMenu
        && (
        <UserMenu
          setViewProfile={setViewProfile}
          handleMenuClickAway={handleMenuClickAway}
          anchorEl={anchorEl}
          open={open}
          monogramVariant={monogramVariant}
          userDetails={userDetails}
          isImpersonated={isImpersonated}
        />
        )}
    </div>
  );
};

UserProfile.defaultProps = {
  monogramVariant: 'small',
  withUserMenu: false,
  setViewProfile: null,
  userProfileError: false,
  isGuestUser: false,
};

UserProfile.propTypes = {
  /**
   * Variant of monogram
   * @property {string} monogramVariant
   */
  monogramVariant: PropTypes.oneOf(['micro', 'small', 'medium', 'large']),

  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape().isRequired,

  /**
   * Details of the initiator when impersonation is done
   * @property {Object} initiatorDetails
   */
  initiatorDetails: PropTypes.shape().isRequired,

  /**
   * store value indicating whether the user is impersonated or not
   * @property {Boolean} isImpersonated
   */
  isImpersonated: PropTypes.bool.isRequired,

  /**
   * Indicates whether the component should have user menu or not
   * @property {bool} withUserMenu
   */
  withUserMenu: PropTypes.bool,

  /**
   * function to show the view profile panel
   * @property {function} setViewProfile
   */
  setViewProfile: PropTypes.func,

  /**
   * Indicates whether there's a user profile error
   * @property {bool} userProfileError
   */
  userProfileError: PropTypes.bool,

  /**
   * Guest user (isResourceShared && !isEvUser): show "--" avatar, menu with only Logout. #genai
   * @property {bool} isGuestUser
   */
  isGuestUser: PropTypes.bool,

  t: PropTypes.func.isRequired,
};

export default compose(withTranslation())(UserProfile);
